import { Loading } from "react-admin";

import { AccountInfo, PublicClientApplication } from "@azure/msal-browser";
import { Box } from "@mui/material";
import { ReactNode, useEffect, useRef, useState } from "react";

type Props = { instance: PublicClientApplication; children: ReactNode };

function AuthGate({ instance, children }: Props) {
  const [ready, setReady] = useState(false);
  const [authed, setAuthed] = useState(false);
  const loginStartedRef = useRef(false); // guard against StrictMode double-effect

  useEffect(() => {
    let mounted = true;

    (async () => {
      // 1) Always resolve any pending redirect first
      await instance.handleRedirectPromise().catch(() => {
        /* ignore */
      });

      // 2) If we already have an account, set it active and continue
      const accounts = instance.getAllAccounts();
      if (accounts.length > 0) {
        const active = instance.getActiveAccount() ?? accounts[0];
        instance.setActiveAccount(active as AccountInfo);
        if (mounted) {
          setAuthed(true);
          setReady(true);
        }
        return;
      }

      // 3) No account: if an interaction is already in progress, just wait
      const ip = instance.getActiveAccount() ?? undefined;
      const inProgress = ip && ip !== undefined;
      if (inProgress) {
        if (mounted) setReady(true); // let MSAL finish the redirect
        return;
      }

      // 4) Kick off login redirect exactly once
      if (!loginStartedRef.current) {
        loginStartedRef.current = true;
        await instance.loginRedirect(); // will leave the page
      }

      if (mounted) setReady(true);
    })();

    return () => {
      mounted = false;
    };
  }, [instance]);

  if (!ready) {
    // Your full-screen loader
    return (
      <Box width="100%" height="100%">
        <Loading />
      </Box>
    );
  }

  return <>{authed ? children : null}</>;
}

export default AuthGate;
